// US1945335: Global Feature Toggle admin UI
import { useCallback, useState } from "react";
import { FaSync, FaPowerOff } from "react-icons/fa";
import ConfirmationModal from "../Modern/ConfirmationModal";
import RefreshIndicator from "../Modern/Incidents/components/RefreshIndicator";
import useGlobalToggles from "../../hooks/useGlobalToggles";
import ModernUILaunchToggle from "./ModernUILaunchToggle";
import styles from "./GlobalTogglesList.module.css";

// ---------------------------------------------------------------------------
// Skeleton loader — shown only during initial load
// ---------------------------------------------------------------------------
function SkeletonLoader() {
  return (
    <div className={styles.skeleton} aria-hidden="true">
      {[...Array(3)].map((_, i) => (
        <div key={i} className={styles.skeletonRow}>
          <div className={styles.skeletonCell} style={{ flex: 2 }} />
          <div className={styles.skeletonCell} style={{ flex: 1 }} />
          <div className={styles.skeletonCell} style={{ flex: 1 }} />
          <div className={styles.skeletonCell} style={{ flex: 0.5 }} />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

/**
 * GlobalTogglesList — Admin UI for global feature toggles.
 *
 * Security: the deity toggle table renders only for deity users; the Modern UI
 * soft-launch toggle (US2028928) renders only for launch admins. Each section's
 * data fetch lives in its own child component, so a launch admin who is not a
 * deity never hits the deity-only toggles endpoint (and vice versa). The parent
 * tab must also guard on (deity || launch admin) before mounting this component.
 *
 * @param {Object} props
 * @param {Object} props.userPrivileges - Privilege object from the current user
 * @param {boolean} props.isModernUILaunchAdmin - modern_ui_launch_admin flag
 */
function GlobalTogglesList({ userPrivileges, isModernUILaunchAdmin }) {
  const isDeity = Boolean(userPrivileges?.deity);

  // UX-layer access gate — render nothing unless the user can operate at least
  // one section. The backend independently enforces both gates.
  if (!isDeity && !isModernUILaunchAdmin) {
    return null;
  }

  return (
    <>
      {isDeity && <GlobalTogglesListInner />}
      {isModernUILaunchAdmin && <ModernUILaunchToggle isLaunchAdmin />}
    </>
  );
}

// Inner component — only rendered when deity check passes
function GlobalTogglesListInner() {
  const { toggles, loading, refreshing, lastUpdated, error, refresh, flip } =
    useGlobalToggles({ enabled: true });

  // Confirmation modal state
  const [pendingFlip, setPendingFlip] = useState(null); // { name, currentEnabled }
  const [showModal, setShowModal] = useState(false);

  // Per-toggle submitting state (name → true while PATCH in flight)
  const [submitting, setSubmitting] = useState({});

  // Async errors (PATCH failures after modal confirmation)
  const [flipError, setFlipError] = useState(null);

  // User clicks the toggle button → open confirmation, don't flip yet
  const handleFlipRequest = useCallback((name, currentEnabled) => {
    setPendingFlip({ name, currentEnabled });
    setShowModal(true);
    setFlipError(null);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setPendingFlip(null);
  }, []);

  // User confirmed in modal → perform the PATCH. The modal is closed in every
  // outcome (success or failure). Errors surface in the page-level banner so
  // the user can see what went wrong and retry without the modal blocking the
  // UI. (US1945335 PR 401 review — H4.)
  const handleFlipConfirm = useCallback(async () => {
    if (!pendingFlip) return;
    const { name, currentEnabled } = pendingFlip;
    const newEnabled = !currentEnabled;

    setSubmitting((prev) => ({ ...prev, [name]: true }));
    setFlipError(null);
    try {
      await flip(name, newEnabled);
    } catch (err) {
      setFlipError(err.message || "Failed to update toggle. Please try again.");
    } finally {
      setSubmitting((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
      closeModal();
    }
  }, [pendingFlip, flip, closeModal]);

  // Build sorted toggle entries for rendering
  const toggleEntries = Object.entries(toggles).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  // ---------------------------------------------------------------------------
  // Initial loading skeleton
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <p className={styles.loadingText} aria-live="polite">
          Loading global feature toggles...
        </p>
        <SkeletonLoader />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Error state
  // ---------------------------------------------------------------------------
  if (error) {
    return (
      <div className={styles.alertDanger} role="alert">
        <h3 className={styles.alertHeading}>Error Loading Global Toggles</h3>
        <p className={styles.alertText}>{error.message || String(error)}</p>
        <button
          className={`${styles.button} ${styles.buttonOutline}`}
          onClick={refresh}
        >
          Try Again
        </button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Confirmation modal message
  // ---------------------------------------------------------------------------
  const confirmMessage = pendingFlip
    ? pendingFlip.currentEnabled
      ? `This will disable the "${pendingFlip.name}" feature flag globally. All regions will stop using it within 30 seconds. Continue?`
      : `This will enable the "${pendingFlip.name}" feature flag globally across all regions within 30 seconds. Continue?`
    : "";

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className={styles.container}>
      {/* Live region for async update announcements */}
      <div
        className={styles.visuallyHidden}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {refreshing ? "Refreshing global feature toggles..." : ""}
      </div>

      {/* Flip error banner */}
      {flipError && (
        <div className={styles.alertDanger} role="alert">
          <span className={styles.alertText}>{flipError}</span>
          <button
            className={styles.bannerDismiss}
            onClick={() => setFlipError(null)}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h2 className={styles.title}>Global Feature Toggles</h2>
          <p className={styles.description}>
            Manage system-wide feature flags. Changes propagate to all regions
            within 30 seconds.
          </p>
        </div>
        <div className={styles.actions}>
          <RefreshIndicator
            isRefreshing={refreshing}
            lastUpdated={lastUpdated}
            showTimestamp={!refreshing && !!lastUpdated}
          />
          <button
            className={`${styles.buttonIcon} ${styles.buttonOutline}`}
            onClick={refresh}
            title="Refresh toggles"
            aria-label="Refresh global feature toggles"
            disabled={refreshing}
          >
            <FaSync aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Desktop table (≥768px)                                              */}
      {/* ------------------------------------------------------------------ */}
      <table className={styles.table} aria-label="Global feature toggles">
        <thead>
          <tr>
            <th scope="col">Toggle Name</th>
            <th scope="col">Status</th>
            <th scope="col">Last Updated</th>
            <th scope="col">Updated By</th>
            <th scope="col">
              <span className={styles.visuallyHidden}>Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {toggleEntries.length > 0 ? (
            toggleEntries.map(([name, toggle]) => {
              const isEnabled = Boolean(toggle?.enabled);
              const isSubmitting = Boolean(submitting[name]);

              return (
                <tr key={name} className={styles.tableRow}>
                  <td className={styles.tableCell}>
                    <span className={styles.toggleName}>{name}</span>
                    {toggle?.description && (
                      <span className={styles.toggleDescription}>
                        {toggle.description}
                      </span>
                    )}
                  </td>
                  <td className={styles.tableCell}>
                    <span
                      className={`${styles.badge} ${
                        isEnabled ? styles.badgeEnabled : styles.badgeDisabled
                      }`}
                    >
                      {isEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td className={styles.tableCell}>
                    {formatDate(toggle?.updatedAt)}
                  </td>
                  <td className={styles.tableCell}>
                    {toggle?.updatedBy || "—"}
                  </td>
                  <td className={styles.tableCell} data-label="Actions">
                    <div className={styles.actionButtons}>
                      <button
                        type="button"
                        className={`${styles.iconButton} ${
                          isEnabled
                            ? styles.iconButtonDanger
                            : styles.iconButtonSuccess
                        }`}
                        onClick={() =>
                          handleFlipRequest(name, isEnabled)
                        }
                        disabled={isSubmitting}
                        title={
                          isEnabled
                            ? `Disable ${name}`
                            : `Enable ${name}`
                        }
                        aria-label={
                          isEnabled
                            ? `Disable ${name} (opens confirmation)`
                            : `Enable ${name} (opens confirmation)`
                        }
                        aria-haspopup="dialog"
                      >
                        {isSubmitting ? (
                          <span
                            className={styles.spinnerSmall}
                            aria-label="Updating..."
                          />
                        ) : (
                          <FaPowerOff aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan="5"
                className={`${styles.tableCell} ${styles.emptyCell}`}
              >
                No global feature toggles are registered. Toggles are added
                by deploying code that registers them with the backend
                registry.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ------------------------------------------------------------------ */}
      {/* Mobile card list (<768px)                                           */}
      {/* ------------------------------------------------------------------ */}
      <div className={styles.cardList} aria-label="Global feature toggles">
        {toggleEntries.length > 0 ? (
          toggleEntries.map(([name, toggle]) => {
            const isEnabled = Boolean(toggle?.enabled);
            const isSubmitting = Boolean(submitting[name]);

            return (
              <div key={name} className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.cardTitle}>{name}</span>
                  <span
                    className={`${styles.badge} ${
                      isEnabled ? styles.badgeEnabled : styles.badgeDisabled
                    }`}
                  >
                    {isEnabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
                {toggle?.description && (
                  <p className={styles.cardDescription}>
                    {toggle.description}
                  </p>
                )}
                <div className={styles.cardBody}>
                  <div className={styles.cardField}>
                    <span className={styles.cardLabel}>Last Updated</span>
                    <span className={styles.cardValue}>
                      {formatDate(toggle?.updatedAt)}
                    </span>
                  </div>
                  <div className={styles.cardField}>
                    <span className={styles.cardLabel}>Updated By</span>
                    <span className={styles.cardValue}>
                      {toggle?.updatedBy || "—"}
                    </span>
                  </div>
                </div>
                <div className={styles.cardActions}>
                  <button
                    type="button"
                    className={`${styles.cardButton} ${
                      isEnabled
                        ? styles.cardButtonDanger
                        : styles.cardButtonSuccess
                    }`}
                    onClick={() => handleFlipRequest(name, isEnabled)}
                    disabled={isSubmitting}
                    aria-label={
                      isEnabled
                        ? `Disable ${name} (opens confirmation)`
                        : `Enable ${name} (opens confirmation)`
                    }
                    aria-haspopup="dialog"
                  >
                    {isSubmitting ? (
                      <span
                        className={styles.spinnerSmall}
                        aria-label="Updating..."
                      />
                    ) : (
                      <FaPowerOff aria-hidden="true" />
                    )}
                    {isEnabled ? "Disable" : "Enable"}
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className={styles.emptyState}>
            No global feature toggles are registered.
          </div>
        )}
      </div>

      {/* Summary count */}
      {toggleEntries.length > 0 && (
        <div className={styles.summary}>
          <small>
            {toggleEntries.length} toggle
            {toggleEntries.length !== 1 ? "s" : ""} —{" "}
            {toggleEntries.filter(([, t]) => t?.enabled).length} enabled
          </small>
        </div>
      )}

      {/* Confirmation modal */}
      <ConfirmationModal
        isOpen={showModal}
        onClose={closeModal}
        onConfirm={handleFlipConfirm}
        title={
          pendingFlip?.currentEnabled
            ? "Disable Feature Toggle"
            : "Enable Feature Toggle"
        }
        message={confirmMessage}
        confirmText={pendingFlip?.currentEnabled ? "Disable" : "Enable"}
        cancelText="Cancel"
        variant={pendingFlip?.currentEnabled ? "danger" : "warning"}
      />
    </div>
  );
}

export default GlobalTogglesList;
